<template>
    <section
        class="slice slice-testimonials slice-testimonials--grid"
        :data-mui-theme="theme"
        aria-label="testimonials"
        data-testid="testimonials-grid-slice"
    >
        <div class="slice-content">
            <PrismicRichText
                v-if="isFilled.richText(slice.primary.title)"
                :field="slice.primary.title"
                class="slice-testimonials--grid__title mui-title"
                data-testid="testimonials-grid-title"
            />

            <div v-if="slice.primary.items?.length" class="slice-testimonials--grid__grid">
                <div
                    v-for="(item, index) in slice.primary.items"
                    :key="index"
                    :class="['slice-testimonials--grid__item', `slice-testimonials--grid__item--${item.cardSize}`]"
                    :data-testid="`testimonials-grid-item--${index}`"
                >
                    <component :is="item.cardType === 'quote' ? TestimonialsGridQuoteCard : TestimonialsGridImageCard" :item="item" />
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps} from '@prismicio/vue';
    import {PrismicRichText} from '@prismicio/vue';
    import type {TestimonialsSlice} from '@prismicio-types';
    import TestimonialsGridImageCard from '../components/TestimonialsGridImageCard.vue';
    import TestimonialsGridQuoteCard from '../components/TestimonialsGridQuoteCard.vue';

    type TestimonialsGridInZone = Extract<TestimonialsSlice, {variation: 'testimonialsGrid'}>;

    const props = defineProps(getSliceComponentProps<TestimonialsGridInZone>());

    const theme = computed(() => props.slice.primary.theme);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-testimonials--grid {
        .slice-content {
            text-align: left;
        }

        &__title {
            margin-bottom: var(--joy-core-spacing-8);
            @include mq.screen_md(min) {
                margin-bottom: 64px;
            }
        }

        &__grid {
            display: grid;
            gap: var(--joy-core-spacing-4);
            grid-template-columns: repeat(3, minmax(288px, 1fr));
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;

            &::-webkit-scrollbar {
                display: none;
            }

            @include mq.screen_md(min) {
                grid-template-columns: repeat(3, 1fr);
                grid-auto-rows: minmax(220px, auto);
                gap: var(--joy-core-spacing-6);
                grid-auto-flow: dense;
                overflow: visible;
            }
        }

        &__item {
            height: 100%;

            > * {
                height: 100%;
            }

            &--small {
                grid-column: span 1;
                grid-row: span 1;
            }

            &--medium {
                grid-column: span 2;
                grid-row: span 1;
            }

            &--large {
                grid-column: span 2;
                grid-row: span 2;
            }

            &--extra-large {
                grid-column: span 3;
                grid-row: span 1;
            }
        }

        &__image-card--has-link,
        &__quote-card--has-link {
            cursor: pointer;
            transition: transform 0.2s ease-in-out;

            @include mq.screen_md(min) {
                &:hover {
                    transform: scale(1.01);
                }

                @media (prefers-reduced-motion: reduce) {
                    transition: none;

                    &:hover {
                        transform: none;
                    }
                }
            }
        }
    }
</style>
